// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import {
  KitThreadCheckController,
  KitThreadCheckPhase,
  isFailedState,
} from 'hooks/useKitThreadCheck';
import {
  KitThreadCheckJob,
  KitThreadCheckKind,
  KitThreadCheckStatus,
  submitKitThreadCheckFeedback,
} from 'lib/api/kitThreadCheck';
import { Maybe } from 'types/UtilityTypes';

import {
  KitPosthogEvents,
  captureKitThreadCheckFeedback,
  getKitThreadCheckRequestedLogProperties,
  getPosthogCapture,
} from '../utils/posthogEvents';
import { KitFeedback, KitFeedbackRating } from './KitFeedback';

export const KIT_THREAD_CHECK_FAILED_MESSAGE =
  "Kit couldn't finish checking this thread.";

type KitThreadCheckMessageProps = {
  check: KitThreadCheckController;
};

// Kit bubble for a thread check the rep asked for: progress, result, or failure.
export function KitThreadCheckMessage({ check }: KitThreadCheckMessageProps) {
  const posthog = usePostHog();
  const view = getMessageView(check);
  if (!view) {
    return null;
  }

  const { message, progress, canRetry, resultJob } = view;
  const foundCount = resultJob?.result?.newSuggestionCount ?? 0;
  const progressPercent =
    progress && progress.total > 0
      ? Math.round((progress.completed / progress.total) * 100)
      : 0;

  const handleFeedback = (
    job: KitThreadCheckJob,
    rating: KitFeedbackRating,
    note: string
  ) => {
    const helpful = rating === KitFeedbackRating.Up;
    captureKitThreadCheckFeedback({
      capture: getPosthogCapture(posthog),
      job,
      helpful,
      hasComment: note.trim() !== '',
    });
    return submitKitThreadCheckFeedback({
      jobID: job.jobId,
      helpful,
      comment: note,
    });
  };

  return (
    <div
      role={canRetry ? 'alert' : 'status'}
      data-testid='kit-thread-check-message'
      className='w-full rounded-xl rounded-tl-sm border border-neutral-400 bg-neutral-100 px-3 py-2'
    >
      <Flex direction='col' gap='xs' className='w-full pl-2'>
        <Flex align='center' justify='between' gap='sm' className='w-full'>
          <Typography
            as='span'
            variant='body-xs'
            weight='medium'
            className='block text-neutral-800'
          >
            {message}
          </Typography>
          {canRetry && (
            <Button
              type='button'
              buttonNamePosthog={KitPosthogEvents.ThreadCheckRequested}
              logProperties={getKitThreadCheckRequestedLogProperties({
                kind: check.kind,
                threadId: check.threadId,
                emailId: check.emailId,
                isRetry: true,
              })}
              variant='unstyled'
              size='auto'
              onClick={check.apply}
              className='h-7 shrink-0 rounded-[4px] border border-neutral-400 bg-neutral-50 px-2 text-[12px] font-medium text-neutral-800 hover:border-accent-400 hover:text-accent-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent-400'
            >
              Retry
            </Button>
          )}
        </Flex>

        {progress && (
          <div
            role='progressbar'
            aria-label='Kit thread check progress'
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.completed}
            className='h-0.5 w-full overflow-hidden rounded-full bg-accent-100'
          >
            <div
              className='h-full rounded-full bg-accent-500 transition-[width] duration-500'
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}

        {resultJob && (
          <KitFeedback
            key={resultJob.jobId}
            upLabel='Kit got this right'
            downLabel={
              foundCount > 0 ? 'Kit got this wrong' : 'Kit missed something'
            }
            upPlaceholder='Anything to add? (optional)'
            downPlaceholder={
              foundCount > 0
                ? 'What did Kit get wrong? (optional)'
                : 'What did Kit miss? (optional)'
            }
            buttonNamePosthog='kit_thread_check_feedback_submit'
            isAlreadySubmitted={resultJob.feedback.helpful !== null}
            testId='kit-thread-check-feedback'
            onSubmit={(rating, note) => handleFeedback(resultJob, rating, note)}
          />
        )}
      </Flex>
    </div>
  );
}

type MessageView = {
  message: string;
  progress?: { completed: number; total: number };
  canRetry?: boolean;
  // Set for a finished check, which the rep can rate.
  resultJob?: KitThreadCheckJob;
};

function getMessageView(check: KitThreadCheckController): Maybe<MessageView> {
  const { state, kind } = check;
  if (isFailedState(state)) {
    return { message: KIT_THREAD_CHECK_FAILED_MESSAGE, canRetry: true };
  }
  if (state.phase === KitThreadCheckPhase.Starting) {
    return {
      message: getProgressMessage(kind, 0, 0),
      progress: { completed: 0, total: 0 },
    };
  }
  if (state.phase !== KitThreadCheckPhase.Job) {
    return null;
  }

  const { job } = state;
  if (check.isRunning) {
    const total = job.progress.total;
    const completed = Math.min(job.progress.completed, total);
    return {
      message: getProgressMessage(kind, completed, total),
      progress: { completed, total },
    };
  }
  if (job.status !== KitThreadCheckStatus.Succeeded) {
    return null;
  }
  return { message: getResultMessage(job), resultJob: job };
}

const KIND_NOUN: Record<KitThreadCheckKind, { one: string; many: string }> = {
  [KitThreadCheckKind.QuoteRequest]: {
    one: 'quote request',
    many: 'quote requests',
  },
  [KitThreadCheckKind.LoadBuilding]: {
    one: 'load to build',
    many: 'loads to build',
  },
};

export function getProgressMessage(
  kind: KitThreadCheckKind,
  completed: number,
  total: number
): string {
  const base = `Checking this thread for ${KIND_NOUN[kind].many}...`;
  if (total <= 0) {
    return base;
  }
  return `${base} ${completed} of ${total} ${pluralize(total, 'email', 'emails')}`;
}

export function getResultMessage(job: KitThreadCheckJob): string {
  const noun = KIND_NOUN[job.kind];
  const foundCount = job.result?.newSuggestionCount ?? 0;
  if (foundCount > 0) {
    return `Found ${foundCount} new ${pluralize(foundCount, noun.one, noun.many)} in this thread.`;
  }

  const checked = job.result?.emailsChecked ?? job.progress.completed;
  return `Kit checked ${checked} ${pluralize(checked, 'email', 'emails')} and didn't find any new ${noun.many}.`;
}

function pluralize(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}
